import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

/**
 * Single source of truth for compliance statistics.
 * Calls the calculate_compliance_stats RPC which handles all compliance calculations
 * in the database layer for consistency across all displays.
 *
 * Supports both single project and stack (multiple projects) views.
 *
 * @param {string} projectId - The space/project ID to get compliance stats for (single project mode)
 * @param {object} options - Optional configuration
 * @param {boolean} options.enabled - Whether to fetch data (default: true)
 * @param {number} options.refetchInterval - Auto-refetch interval in ms (default: null)
 * @param {string[]} options.projectIds - Array of project IDs for stack view (overrides projectId)
 * @param {boolean} options.isStackView - Whether this is a stack view (default: false)
 * @returns {object} { data, loading, error, refetch }
 *
 * Data structure:
 * {
 *   overall: {
 *     compliance_percentage: number,
 *     valid: number,
 *     valid_cross_space: number,
 *     expiring: number,
 *     expiring_cross_space: number,
 *     expired: number,
 *     missing: number,
 *     empty: number,
 *     total_requirements: number,
 *     total_team_members: number,
 *     total_cells: number
 *   },
 *   by_requirement: [{
 *     requirement_id: string,
 *     name: string,
 *     required: number,
 *     is_document_requirement: boolean,
 *     valid: number,
 *     valid_cross_space: number,
 *     expiring: number,
 *     expiring_cross_space: number,
 *     expired: number,
 *     missing: number,
 *     empty: number,
 *     assigned_count: number,
 *     compliant_count: number,
 *     compliance_percentage: number | null
 *   }],
 *   by_user: [{
 *     user_id: string,
 *     space_member_id: string,
 *     name: string,
 *     email: string,
 *     avatar_url: string,
 *     assigned_count: number,
 *     valid: number,
 *     expiring: number,
 *     expired: number,
 *     missing: number,
 *     valid_cross_space: number,
 *     expiring_cross_space: number,
 *     compliance_percentage: number | null
 *   }],
 *   cells: [{
 *     user_id: string,
 *     space_member_id: string,
 *     user_name: string,
 *     requirement_id: string,
 *     requirement_name: string,
 *     is_assigned: boolean,
 *     cell_status: 'valid' | 'expiring' | 'expired' | 'missing' | 'valid_cross_space' | 'expiring_cross_space' | 'empty',
 *     is_cross_space: boolean,
 *     expiry_date: string | null,
 *     qualification_id: string | null
 *   }]
 * }
 */
export function useComplianceStats(projectId, options = {}) {
  const {
    enabled = true,
    refetchInterval = null,
    projectIds = null,
    isStackView = false
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine if we should fetch - need either projectId OR projectIds for stack view
  const shouldFetch = enabled && (projectId || (isStackView && projectIds?.length > 0));

  const fetchStats = useCallback(async () => {
    if (!shouldFetch) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let result;
      let rpcError;

      if (isStackView && projectIds?.length > 0) {
        // Stack view: use the stack compliance stats function
        const response = await supabase.rpc(
          'calculate_stack_compliance_stats',
          { p_project_ids: projectIds }
        );
        result = response.data;
        rpcError = response.error;
      } else if (projectId) {
        // Single project view
        const response = await supabase.rpc(
          'calculate_compliance_stats',
          { p_project_id: projectId }
        );
        result = response.data;
        rpcError = response.error;
      }

      if (rpcError) {
        throw rpcError;
      }

      setData(result);
    } catch (err) {
      console.error('[useComplianceStats] Error fetching compliance stats:', err);
      setError(err.message || 'Failed to fetch compliance stats');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, projectIds, isStackView, shouldFetch]);

  // Initial fetch
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Optional refetch interval
  useEffect(() => {
    if (!refetchInterval || !shouldFetch) return;

    const interval = setInterval(fetchStats, refetchInterval);
    return () => clearInterval(interval);
  }, [refetchInterval, shouldFetch, fetchStats]);

  return {
    data,
    loading,
    error,
    refetch: fetchStats
  };
}

/**
 * Helper to get combined valid count (assigned + cross-space)
 */
export function getTotalValid(stats) {
  if (!stats?.overall) return 0;
  return (stats.overall.valid || 0) + (stats.overall.valid_cross_space || 0);
}

/**
 * Helper to get combined expiring count (assigned + cross-space)
 */
export function getTotalExpiring(stats) {
  if (!stats?.overall) return 0;
  return (stats.overall.expiring || 0) + (stats.overall.expiring_cross_space || 0);
}

/**
 * Helper to get total compliant count (valid + expiring, both assigned and cross-space)
 */
export function getTotalCompliant(stats) {
  return getTotalValid(stats) + getTotalExpiring(stats);
}

/**
 * Helper to format compliance percentage for display
 */
export function formatCompliancePercentage(percentage) {
  if (percentage === null || percentage === undefined) return 'N/A';
  return `${Math.round(percentage)}%`;
}

/**
 * Helper to get compliance status color class
 */
export function getComplianceColor(percentage) {
  if (percentage === null || percentage === undefined) return 'text-muted-foreground';
  if (percentage >= 80) return 'text-green-600';
  if (percentage >= 50) return 'text-yellow-600';
  return 'text-red-600';
}

/**
 * Helper to get compliance status background color class
 */
export function getComplianceBgColor(percentage) {
  if (percentage === null || percentage === undefined) return 'bg-muted';
  if (percentage >= 80) return 'bg-green-100';
  if (percentage >= 50) return 'bg-yellow-100';
  return 'bg-red-100';
}
